/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: garry彭
 * @LastEditTime: 2024-04-24 16:05:28
 */
import React, { useState } from 'react'
import { AutoComplete, Form, Input, Modal, Radio, message } from 'antd'
import {
  invoiceType as InvoiceType,
  emailReg,
  phoneReg,
  taxNoReg,
  InvoiceTypeValue
} from '../lib/constants'
import { useAppDispatch, useUserId } from '../../../app/hooks'
import { fetchTitleList, applyInvoice } from '../../../actions/invoice'
import styles from './index.module.scss'
import AntdProvider from '../../../components/AntdProvider'

// 组织信息类型
interface OrgInfo {
  name: string
  taxId: string
  location: string
}

// 组件 Props 类型
interface ApplyProps {
  visible: boolean
  onClose: () => void
  onConfirm: () => void
  orderNoList: string[]
  amount: number
}

const Apply: React.FC<ApplyProps> = ({ visible, onClose, onConfirm, orderNoList, amount }) => {
  const [form] = Form.useForm()
  const [invoiceType, setInvoiceType] = useState<InvoiceTypeValue>(InvoiceType.company)
  const [orgList, setOrgList] = useState<OrgInfo[]>([])
  const dispatch = useAppDispatch()
  const userID = useUserId()
  const [loading, setLoading] = useState(false)

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)
      const { invoiceType: _, ...params } = values

      params.buyerAddressPhone = `${values.buyerAddress || ''}${
        values.phone || ''
      }`
      params.buyerBankAccount = `${values.buyerBankName || ''}${
        values.buyerBankNumber || ''
      }`
      params.orderNo = orderNoList
      params.userID = userID

      dispatch(applyInvoice(params))
        .then(() => {
          message.success('提交成功，请注意查收。')
          setLoading(false)
          onClose()
          onConfirm()
        })
        .catch((e) => {
          console.log(e)
          setLoading(false)
          message.error('开票异常，请前往未开票列表重新开票')
        })
    } catch (e) {
      // 表单验证失败
    }
  }

  // AutoComplete 在 antd 6 中的 options 格式
  const autoCompleteOptions = orgList.map((item) => ({
    value: item.name,
    label: item.name
  }))

  return (
    <AntdProvider>
      <Modal
        open={visible}
        confirmLoading={loading}
        styles={{
          body: {
            maxHeight: 600,
            overflowY: 'scroll'
          }
        }}
        title={
          <>
            <div>
              申请电子发票
              <span className={styles['apply-tip']}>
                （开票受理时间：工作日 10:00-20:30）{' '}
              </span>
            </div>
            <div className={styles['apply-subtitle']}>
              <span>订单号：{orderNoList[0]}</span>
              <span className={styles['apply-amount']}>订单金额：{amount}</span>
            </div>
          </>
        }
        okText="提交"
        cancelText="取消"
        onCancel={onClose}
        onOk={handleOk}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ invoiceType: InvoiceType.company }}
        >
          <Form.Item
            label="发票抬头类型"
            name="invoiceType"
            rules={[{ required: true }]}
          >
            <Radio.Group
              onChange={(e) => {
                setInvoiceType(e.target.value)
              }}
            >
              <Radio value={InvoiceType.company}>单位</Radio>
              <Radio value={InvoiceType.person}>个人/事业单位</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="发票抬头"
            name="buyerName"
            rules={[
              {
                required: true,
                message: '请填写正确购买方发票抬头'
              }
            ]}
          >
            <AutoComplete
              placeholder="请填写购买方发票抬头"
              options={autoCompleteOptions}
              onSelect={(value) => {
                const item = orgList.find((org) => org.name === value)

                if (item) {
                  form.setFieldsValue({
                    buyerTaxNo: item.taxId,
                    buyerAddress: item.location
                  })
                }
              }}
              onSearch={(text) => {
                dispatch(
                  fetchTitleList({
                    organizationName: text
                  })
                ).then((res) => {
                  setOrgList(res.resultData)
                })
              }}
            />
          </Form.Item>

          <Form.Item
            label="纳税人识别号"
            name="buyerTaxNo"
            rules={[
              {
                required: invoiceType === InvoiceType.company,
                message: '请填写正确购买方纳税人识别号'
              },
              {
                validator: async (_, value) => {
                  if (value && !taxNoReg.test(value)) {
                    throw new Error(
                      '请填写正确购买方纳税人识别号，只能包含数字和大小写字母'
                    )
                  }
                }
              }
            ]}
          >
            <Input placeholder="请填写购买方纳税人识别号" />
          </Form.Item>

          <Form.Item
            label="收票人邮箱"
            name="buyerEmail"
            rules={[
              {
                required: true,
                message: '请填写邮箱'
              },
              {
                validator: async (_, value) => {
                  if (value && !emailReg.test(value)) {
                    throw new Error('邮箱格式不正确')
                  }
                }
              }
            ]}
          >
            <Input placeholder="请填写收票人邮箱" />
          </Form.Item>

          <Form.Item
            label="收票人手机号"
            name="buyerPhone"
            rules={[
              {
                validator: async (_, value) => {
                  if (value && !phoneReg.test(value)) {
                    throw new Error('手机号格式不正确')
                  }
                }
              }
            ]}
          >
            <Input placeholder="请填写收票人手机号" />
          </Form.Item>

          <Form.Item label="地址" name="buyerAddress">
            <Input placeholder="请填写准确地址" />
          </Form.Item>
          <Form.Item label="电话" name="phone">
            <Input placeholder="请填写7位或以上电话号码" />
          </Form.Item>
          <Form.Item label="开户银行" name="buyerBankName">
            <Input placeholder="请填写购买方开户行" />
          </Form.Item>
          <Form.Item label="银行账号" name="buyerBankNumber">
            <Input placeholder="请填写购买方银行账号" />
          </Form.Item>
          <Form.Item
            label="备注（请谨慎填写，此内容会展示在发票备注栏）"
            name="exts"
          >
            <Input placeholder="请填写需在备注栏中显示的信息" />
          </Form.Item>
        </Form>
      </Modal>
    </AntdProvider>
  )
}

export default Apply
